/**
 * Procedural audio via the Web Audio API — no asset files, works offline.
 * Reuses the AudioContext Phaser created (unlocked by the title-screen tap).
 *
 * SFX: soft sine/triangle blips (dry).
 * Music: a generative cozy score that shifts with the SEASON — layered pad +
 *        bass + motif-driven melody + bells + soft percussion, run through a
 *        generated convolution reverb. Each season has its own scale, tempo,
 *        chord progression and instrumentation.
 */
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let musicBus: GainNode | null = null;
let reverbWet: GainNode | null = null;
let muted = false; // background mute (visibilitychange)
let sfxOn = true;
let musicOn = true;

const MASTER_VOL = 0.5;

function makeImpulse(c: AudioContext, seconds: number, decay: number): AudioBuffer {
  const rate = c.sampleRate;
  const len = Math.max(1, Math.floor(rate * seconds));
  const buf = c.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
}

export function initAudio(context: AudioContext): void {
  if (ctx) return;
  ctx = context;
  master = ctx.createGain();
  master.gain.value = MASTER_VOL;
  master.connect(ctx.destination);
  try {
    sfxOn = localStorage.getItem('sandyhollow.sfx') !== '0';
    musicOn = localStorage.getItem('sandyhollow.music') !== '0';
  } catch {
    /* ignore */
  }
  // music bus: warmth (low-pass) dry path + a reverb send
  musicBus = ctx.createGain();
  musicBus.gain.value = 0.9;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2700;
  lp.Q.value = 0.4;
  musicBus.connect(lp);
  lp.connect(master);
  const reverb = ctx.createConvolver();
  reverb.buffer = makeImpulse(ctx, 2.6, 3.2);
  reverbWet = ctx.createGain();
  reverbWet.gain.value = 0.2;
  musicBus.connect(reverb);
  reverb.connect(reverbWet);
  reverbWet.connect(master);
}

export function setSfxEnabled(b: boolean): void {
  sfxOn = b;
  try {
    localStorage.setItem('sandyhollow.sfx', b ? '1' : '0');
  } catch {
    /* ignore */
  }
}
export function setMusicEnabled(b: boolean): void {
  musicOn = b;
  try {
    localStorage.setItem('sandyhollow.music', b ? '1' : '0');
  } catch {
    /* ignore */
  }
}
export function isSfxEnabled(): boolean {
  return sfxOn;
}
export function isMusicEnabled(): boolean {
  return musicOn;
}

// ---- SFX (dry) --------------------------------------------------------------

interface Recipe {
  freq: number;
  type: OscillatorType;
  dur: number;
  vol: number;
  slideTo?: number;
  noise?: boolean; // filtered-noise burst instead of an oscillator (splashes, rustles)
  filter?: number; // bandpass centre for noise
}

const RECIPES: Record<string, Recipe[]> = {
  till: [{ freq: 180, type: 'square', dur: 0.1, vol: 0.16, slideTo: 110 }],
  plant: [{ freq: 520, type: 'sine', dur: 0.1, vol: 0.18, slideTo: 660 }],
  water: [{ freq: 840, type: 'sine', dur: 0.14, vol: 0.12, slideTo: 1180 }],
  harvest: [
    { freq: 660, type: 'sine', dur: 0.08, vol: 0.18 },
    { freq: 990, type: 'sine', dur: 0.1, vol: 0.18 },
  ],
  coin: [
    { freq: 988, type: 'square', dur: 0.07, vol: 0.14 },
    { freq: 1319, type: 'square', dur: 0.09, vol: 0.14 },
  ],
  pet: [{ freq: 700, type: 'triangle', dur: 0.14, vol: 0.16, slideTo: 940 }],
  pop: [{ freq: 420, type: 'triangle', dur: 0.08, vol: 0.16, slideTo: 560 }],
  baby: [
    { freq: 900, type: 'triangle', dur: 0.08, vol: 0.15, slideTo: 1150 },
    { freq: 1150, type: 'triangle', dur: 0.07, vol: 0.13, slideTo: 1350 },
    { freq: 1000, type: 'sine', dur: 0.1, vol: 0.12, slideTo: 1250 },
  ],
  babble: [
    { freq: 680, type: 'sine', dur: 0.1, vol: 0.13, slideTo: 840 },
    { freq: 560, type: 'triangle', dur: 0.12, vol: 0.12, slideTo: 700 },
  ],
  moo: [
    { freq: 200, type: 'triangle', dur: 0.3, vol: 0.14, slideTo: 150 },
    { freq: 150, type: 'sine', dur: 0.32, vol: 0.11, slideTo: 120 },
  ],
  baa: [
    { freq: 400, type: 'sawtooth', dur: 0.16, vol: 0.1, slideTo: 340 },
    { freq: 360, type: 'triangle', dur: 0.16, vol: 0.09, slideTo: 310 },
  ],
  oink: [
    { freq: 280, type: 'sawtooth', dur: 0.1, vol: 0.13, slideTo: 170 },
    { freq: 230, type: 'square', dur: 0.09, vol: 0.1, slideTo: 150 },
  ],
  quack: [
    { freq: 620, type: 'sawtooth', dur: 0.09, vol: 0.12, slideTo: 500 },
    { freq: 560, type: 'square', dur: 0.08, vol: 0.1, slideTo: 470 },
  ],
  chop: [
    { freq: 150, type: 'square', dur: 0.1, vol: 0.18, slideTo: 70 },
    { freq: 0, type: 'sine', dur: 0.07, vol: 0.1, noise: true, filter: 700 },
  ],
  weed: [{ freq: 0, type: 'sine', dur: 0.12, vol: 0.12, noise: true, filter: 2600 }],
  chirp: [
    { freq: 2300, type: 'sine', dur: 0.05, vol: 0.09, slideTo: 2900 },
    { freq: 2700, type: 'sine', dur: 0.05, vol: 0.08, slideTo: 2100 },
  ],
  splash: [
    { freq: 0, type: 'sine', dur: 0.18, vol: 0.13, noise: true, filter: 1300 },
    { freq: 320, type: 'sine', dur: 0.1, vol: 0.08, slideTo: 150 },
  ],
  meow: [
    { freq: 700, type: 'sawtooth', dur: 0.18, vol: 0.12, slideTo: 520 },
    { freq: 560, type: 'triangle', dur: 0.16, vol: 0.1, slideTo: 700 },
  ],
  bark: [
    { freq: 300, type: 'square', dur: 0.08, vol: 0.15, slideTo: 180 },
    { freq: 280, type: 'square', dur: 0.08, vol: 0.12, slideTo: 160 },
  ],
  yawn: [
    { freq: 300, type: 'sine', dur: 0.5, vol: 0.12, slideTo: 520 },
    { freq: 500, type: 'sine', dur: 0.45, vol: 0.1, slideTo: 280 },
  ],
  twinkle: [
    { freq: 1200, type: 'sine', dur: 0.08, vol: 0.1, slideTo: 1600 },
    { freq: 1600, type: 'sine', dur: 0.08, vol: 0.09, slideTo: 2000 },
    { freq: 2000, type: 'triangle', dur: 0.14, vol: 0.08, slideTo: 2500 },
  ],
  cricket: [
    { freq: 4000, type: 'square', dur: 0.03, vol: 0.04, slideTo: 4200 },
    { freq: 4100, type: 'square', dur: 0.03, vol: 0.04 },
  ],
  drip: [{ freq: 1400, type: 'sine', dur: 0.09, vol: 0.08, slideTo: 560 }],
  click: [{ freq: 880, type: 'triangle', dur: 0.03, vol: 0.06, slideTo: 1100 }],
};

function sfxTone(r: Recipe, t0: number): void {
  if (!ctx || !master) return;
  if (r.noise) {
    const len = Math.max(1, Math.floor(ctx.sampleRate * r.dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = r.filter ?? 1000;
    bp.Q.value = 0.7;
    const ng = ctx.createGain();
    ng.gain.value = r.vol;
    src.connect(bp);
    bp.connect(ng);
    ng.connect(master);
    src.start(t0);
    src.stop(t0 + r.dur);
    return;
  }
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = r.type;
  osc.frequency.setValueAtTime(r.freq, t0);
  if (r.slideTo) osc.frequency.exponentialRampToValueAtTime(r.slideTo, t0 + r.dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(r.vol, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + r.dur);
  osc.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + r.dur + 0.02);
}

export function play(name: string): void {
  if (!ctx || muted || !sfxOn) return;
  const recipe = RECIPES[name];
  if (!recipe) return;
  let t = ctx.currentTime;
  for (const r of recipe) {
    sfxTone(r, t);
    t += r.dur * 0.6;
  }
}

// ---- Music: seasonal generative score ---------------------------------------

interface Mood {
  root: number; // tonic frequency (Hz)
  scale: number[]; // semitone offsets (one octave) for the melody
  chords: number[][]; // triads as semitone offsets from the tonic
  bpm: number;
  pad: number;
  bell: number;
  perc: boolean;
  reverb: number;
}

const MOODS: Record<string, Mood> = {
  spring: { root: 261.63, scale: [0, 2, 4, 6, 7, 9, 11], bpm: 76, chords: [[0, 4, 7], [5, 9, 12], [9, 12, 16], [7, 11, 14]], pad: 0.045, bell: 0.05, perc: true, reverb: 0.18 },
  summer: { root: 196.0, scale: [0, 2, 4, 5, 7, 9, 11], bpm: 72, chords: [[0, 4, 7], [7, 11, 14], [9, 12, 16], [5, 9, 12]], pad: 0.05, bell: 0.04, perc: true, reverb: 0.16 },
  fall: { root: 220.0, scale: [0, 2, 3, 5, 7, 9, 10], bpm: 66, chords: [[0, 3, 7], [8, 12, 15], [3, 7, 10], [10, 14, 17]], pad: 0.05, bell: 0.045, perc: false, reverb: 0.27 },
  winter: { root: 293.66, scale: [0, 2, 3, 5, 7, 8, 10], bpm: 58, chords: [[0, 3, 7], [7, 10, 14], [8, 12, 15], [3, 7, 10]], pad: 0.04, bell: 0.06, perc: false, reverb: 0.36 },
  festival: { root: 293.66, scale: [0, 2, 4, 5, 7, 9, 11], bpm: 124, chords: [[0, 4, 7], [5, 9, 12], [7, 11, 14], [9, 12, 16]], pad: 0.05, bell: 0.09, perc: true, reverb: 0.2 },
};

interface MotifNote {
  s: number; // scale degree (may exceed 6 → next octave)
  b: number; // beat offset within the 4-beat phrase
  d: number; // duration in beats
}
const MOTIFS: MotifNote[][] = [
  [{ s: 0, b: 0, d: 1 }, { s: 2, b: 1, d: 1 }, { s: 4, b: 2, d: 1.5 }],
  [{ s: 4, b: 0, d: 0.75 }, { s: 3, b: 1, d: 0.75 }, { s: 2, b: 2, d: 1 }, { s: 0, b: 3, d: 1 }],
  [{ s: 0, b: 0.5, d: 0.5 }, { s: 4, b: 1.5, d: 1 }, { s: 7, b: 2.5, d: 1.4 }],
  [{ s: 6, b: 0, d: 1 }, { s: 4, b: 1.5, d: 1 }, { s: 2, b: 3, d: 1 }],
];

let currentMood: Mood = MOODS.spring!;
let musicTimer: ReturnType<typeof setInterval> | null = null;
let nextBeatTime = 0;
let beatCount = 0;

const hz = (root: number, semi: number): number => root * Math.pow(2, semi / 12);

function voice(freq: number, t: number, dur: number, vol: number, type: OscillatorType, attack = 0.02): void {
  if (!ctx || !musicBus) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g);
  g.connect(musicBus);
  osc.start(t);
  osc.stop(t + dur + 0.05);
}

function noiseHit(t: number, vol: number): void {
  if (!ctx || !musicBus) return;
  const len = Math.floor(ctx.sampleRate * 0.05);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const hpf = ctx.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.value = 6500;
  const g = ctx.createGain();
  g.gain.value = vol;
  src.connect(hpf);
  hpf.connect(g);
  g.connect(musicBus);
  src.start(t);
  src.stop(t + 0.06);
}

function scaleSemi(m: Mood, step: number): number {
  const idx = ((step % 7) + 7) % 7;
  return m.scale[idx]! + 12 * Math.floor(step / 7);
}

function scheduleBeat(beat: number, t: number): void {
  const m = currentMood;
  const bd = 60 / m.bpm;
  const chord = m.chords[Math.floor(beat / 4) % m.chords.length]!;

  if (beat % 4 === 0) {
    for (const semi of chord) voice(hz(m.root, semi), t, bd * 4.1, m.pad, 'triangle', 0.5); // pad
    voice(hz(m.root, chord[0]! - 12), t, bd * 3.6, m.pad * 1.25, 'sine', 0.05); // bass
    if (Math.random() < 0.85) {
      const motif = MOTIFS[Math.floor(Math.random() * MOTIFS.length)]!;
      const oct = Math.random() < 0.3 ? 12 : 0;
      for (const n of motif) {
        voice(hz(m.root * 2, scaleSemi(m, n.s) + oct), t + n.b * bd, n.d * bd * 0.9, 0.06, 'sine', 0.02);
      }
    }
  }

  if (m.bell > 0) {
    const tone = chord[beat % chord.length]!;
    voice(hz(m.root * 2, tone), t + bd * 0.5, bd * 0.5, m.bell, 'triangle', 0.01); // bell on the off-beat
  }
  if (m.perc) noiseHit(t + bd * 0.5, 0.035);
}

let baseMood: Mood = MOODS.spring!;
let festivalOn = false;

export function setSeason(id: string): void {
  const m = MOODS[id];
  if (!m) return;
  baseMood = m;
  if (!festivalOn) applyMood(m);
}

/** Swap the score to an upbeat festival tune (and back to the season's mood). */
export function setFestival(on: boolean): void {
  festivalOn = on;
  applyMood(on ? MOODS.festival! : baseMood);
}

function applyMood(m: Mood): void {
  currentMood = m;
  if (reverbWet && ctx) reverbWet.gain.setTargetAtTime(m.reverb, ctx.currentTime, 1.5);
}

export function startMusic(): void {
  if (!ctx || musicTimer || !musicBus) return;
  nextBeatTime = ctx.currentTime + 0.15;
  beatCount = 0;
  const scheduler = (): void => {
    if (!ctx) return;
    if (muted || !musicOn) {
      nextBeatTime = ctx.currentTime + 0.15; // stay fresh so re-enabling doesn't burst
      return;
    }
    while (nextBeatTime < ctx.currentTime + 0.3) {
      scheduleBeat(beatCount, nextBeatTime);
      nextBeatTime += 60 / currentMood.bpm;
      beatCount += 1;
    }
  };
  scheduler();
  musicTimer = setInterval(scheduler, 40);
}

let rainSrc: AudioBufferSourceNode | null = null;
let rainGain: GainNode | null = null;

/** A soft looping rain bed (filtered noise) while it's raining. */
export function setRain(on: boolean): void {
  if (!ctx || !master) return;
  if (on && !rainSrc) {
    const len = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    rainSrc = ctx.createBufferSource();
    rainSrc.buffer = buf;
    rainSrc.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1200;
    bp.Q.value = 0.5;
    rainGain = ctx.createGain();
    rainGain.gain.value = 0.0001;
    rainSrc.connect(bp);
    bp.connect(rainGain);
    rainGain.connect(master);
    rainSrc.start();
    rainGain.gain.setTargetAtTime(0.06, ctx.currentTime, 0.6);
  } else if (!on && rainSrc) {
    if (rainGain) rainGain.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
    const s = rainSrc;
    rainSrc = null;
    rainGain = null;
    setTimeout(() => {
      try {
        s.stop();
      } catch {
        /* ignore */
      }
    }, 900);
  }
}

let oceanSrc: AudioBufferSourceNode | null = null;
let oceanGain: GainNode | null = null;
let oceanLfo: OscillatorNode | null = null;

/** A soft looping ocean bed — low-passed noise with a slow swell (waves rolling in). */
export function setOcean(on: boolean): void {
  if (!ctx || !master) return;
  if (on && !oceanSrc) {
    const len = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    oceanSrc = ctx.createBufferSource();
    oceanSrc.buffer = buf;
    oceanSrc.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 550;
    lp.Q.value = 0.4;
    oceanGain = ctx.createGain();
    oceanGain.gain.value = 0.0001;
    oceanLfo = ctx.createOscillator();
    oceanLfo.frequency.value = 0.17; // ~6s swell
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.03;
    oceanLfo.connect(lfoGain);
    lfoGain.connect(oceanGain.gain);
    oceanSrc.connect(lp);
    lp.connect(oceanGain);
    oceanGain.connect(master);
    oceanSrc.start();
    oceanLfo.start();
    oceanGain.gain.setTargetAtTime(0.05, ctx.currentTime, 0.8);
  } else if (!on && oceanSrc) {
    if (oceanGain) oceanGain.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
    const s = oceanSrc;
    const l = oceanLfo;
    oceanSrc = null;
    oceanGain = null;
    oceanLfo = null;
    setTimeout(() => {
      try {
        s.stop();
        l?.stop();
      } catch {
        /* ignore */
      }
    }, 900);
  }
}

export function setMuted(m: boolean): void {
  muted = m;
  if (master && ctx) master.gain.setTargetAtTime(m ? 0 : MASTER_VOL, ctx.currentTime, 0.02);
}
